// Browser-only image helpers for the XLSX export. Everything is re-encoded
// through a canvas: that normalises webp/svg/... to png/jpeg (the only formats
// Excel accepts here) and downsizes large photos to keep the file small.
// An "excel image" is { base64, extension, width, height } (natural size).

export function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Cannot load image " + url));
    img.src = url;
  });
}

// source: url, <img> or <canvas>
export async function toExcelImage(
  source,
  { maxWidth = 1600, type = "jpeg" } = {}
) {
  const img = typeof source === "string" ? await loadImage(source) : source;
  const naturalW = img.naturalWidth || img.width;
  const naturalH = img.naturalHeight || img.height;
  const ratio = Math.min(1, maxWidth / naturalW);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(naturalW * ratio));
  canvas.height = Math.max(1, Math.round(naturalH * ratio));
  const ctx = canvas.getContext("2d");
  if (type === "jpeg") {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return {
    base64: canvas.toDataURL(`image/${type}`, 0.9),
    extension: type,
    width: naturalW,
    height: naturalH,
  };
}

// Map<product, excel image>; a product whose photo cannot be loaded (404,
// missing CORS headers, ...) gets the fallback picture instead.
export async function loadProductImages(
  products,
  productImageUrl,
  fallbackUrl
) {
  const fallback = await toExcelImage(fallbackUrl, { maxWidth: 400 }).catch(
    () => null
  );
  const images = new Map();
  await Promise.all(
    products.map(async (product) => {
      try {
        images.set(
          product,
          await toExcelImage(productImageUrl(product), { maxWidth: 400 })
        );
      } catch (e) {
        console.warn("XLSX export: product image fallback", e);
        images.set(product, fallback);
      }
    })
  );
  return images;
}
